import type { WsMessage } from "./types";

/**
 * Connection states for the WebSocket session.
 */
export type ConnectionState =
  | "disconnected"
  | "connecting"
  | "connected"
  | "reconnecting";

/**
 * Callbacks provided by the consumer (e.g. App component).
 */
export interface SessionManagerCallbacks {
  onMessage: (msg: WsMessage) => void;
  onConnect: () => void;
  onDisconnect: () => void;
  onStateChange?: (state: ConnectionState) => void;
}

/**
 * Manages a WebSocket connection to the API Gateway with:
 * - Auto-reconnect using exponential backoff (1s, 2s, 4s, 8s, … max 30s)
 * - Session state restoration on reconnect (sends session_id)
 *
 * Validates: Requirements 12.2, 12.5
 */
export class SessionManager {
  private ws: WebSocket | null = null;
  private url: string;
  private callbacks: SessionManagerCallbacks;
  private sessionId: string | null = null;
  private state: ConnectionState = "disconnected";
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private reconnectDelay = 2000;
  private intentionalClose = false;
  private reconnectAttempts = 0;
  private sendQueue: Record<string, unknown>[] = [];
  private keepAliveTimer: ReturnType<typeof setInterval> | null = null;

  static readonly BASE_DELAY = 2000;
  static readonly MAX_DELAY = 60000;
  static readonly MAX_RECONNECT_ATTEMPTS = 10;
  static readonly KEEPALIVE_INTERVAL = 5 * 60 * 1000; // 5 min ping to prevent 10-min idle timeout

  constructor(url: string, callbacks: SessionManagerCallbacks) {
    this.url = url;
    this.callbacks = callbacks;
  }

  /** Current connection state. */
  getState(): ConnectionState {
    return this.state;
  }

  /** Current session ID (set from the first message or restored on reconnect). */
  getSessionId(): string | null {
    return this.sessionId;
  }

  /**
   * Open the WebSocket connection.
   * If already connected or connecting, this is a no-op.
   */
  connect(): void {
    if (this.ws && (this.state === "connected" || this.state === "connecting")) {
      return;
    }
    this.intentionalClose = false;
    this.setState(this.sessionId ? "reconnecting" : "connecting");
    this.createSocket();
  }

  /**
   * Send a JSON-serialisable payload over the WebSocket.
   * If not connected, queues the message and triggers reconnect.
   * Messages are flushed automatically once the connection reopens.
   */
  send(data: Record<string, unknown>): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(data));
      return;
    }
    // Queue and reconnect
    this.sendQueue.push(data);
    console.warn(`WebSocket send queued (state=${this.state}, queue=${this.sendQueue.length})`);
    if (this.state === "disconnected" || this.state === "reconnecting") {
      this.connect();
    }
  }

  /**
   * Cleanly close the connection. Suppresses auto-reconnect.
   */
  disconnect(): void {
    this.intentionalClose = true;
    this.clearReconnectTimer();
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
    this.setState("disconnected");
  }

  // ── private helpers ──────────────────────────────────────────────

  private setState(next: ConnectionState): void {
    this.state = next;
    this.callbacks.onStateChange?.(next);
  }

  private createSocket(): void {
    try {
      this.ws = new WebSocket(this.url);
    } catch {
      this.scheduleReconnect();
      return;
    }

    this.ws.onopen = () => {
      this.setState("connected");
      this.reconnectDelay = SessionManager.BASE_DELAY;
      this.reconnectAttempts = 0; // reset on successful connect
      this.callbacks.onConnect();

      // Restore session subscription on reconnect
      if (this.sessionId && this.ws?.readyState === WebSocket.OPEN) {
        this.ws.send(
          JSON.stringify({
            action: "restore_session",
            session_id: this.sessionId,
          })
        );
      }

      // Flush queued messages
      this.flushQueue();

      // Start keepalive pings to prevent API Gateway 10-min idle timeout
      this.startKeepAlive();
    };

    this.ws.onmessage = (event: MessageEvent) => {
      try {
        const msg: WsMessage = JSON.parse(event.data as string);
        // Track session_id from the first message
        if (msg.session_id && !this.sessionId) {
          this.sessionId = msg.session_id;
        }
        this.callbacks.onMessage(msg);
      } catch {
        // Ignore malformed messages
      }
    };

    this.ws.onclose = () => {
      this.ws = null;
      this.stopKeepAlive();
      if (!this.intentionalClose) {
        this.setState("reconnecting");
        this.callbacks.onDisconnect();
        this.scheduleReconnect();
      }
    };

    this.ws.onerror = () => {
      // The browser fires `onclose` after `onerror`, so reconnect is
      // handled there. Nothing extra needed here.
    };
  }

  /**
   * Schedule a reconnect attempt with exponential backoff.
   * Delays: 1 s → 2 s → 4 s → 8 s → 16 s → 30 s (capped).
   */
  private scheduleReconnect(): void {
    this.clearReconnectTimer();
    if (this.intentionalClose) return;

    this.reconnectAttempts++;
    if (this.reconnectAttempts > SessionManager.MAX_RECONNECT_ATTEMPTS) {
      this.setState("disconnected");
      return; // stop retrying
    }

    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.createSocket();
    }, this.reconnectDelay);

    this.reconnectDelay = Math.min(
      this.reconnectDelay * 2,
      SessionManager.MAX_DELAY
    );
  }

  private clearReconnectTimer(): void {
    if (this.reconnectTimer !== null) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  /** Flush queued messages after reconnect. */
  private flushQueue(): void {
    while (this.sendQueue.length > 0 && this.ws?.readyState === WebSocket.OPEN) {
      const msg = this.sendQueue.shift()!;
      this.ws.send(JSON.stringify(msg));
    }
  }

  /** Send periodic pings to keep the connection alive (prevents API Gateway 10-min idle timeout). */
  private startKeepAlive(): void {
    this.stopKeepAlive();
    this.keepAliveTimer = setInterval(() => {
      if (this.ws?.readyState === WebSocket.OPEN) {
        this.ws.send(JSON.stringify({ action: "ping" }));
      }
    }, SessionManager.KEEPALIVE_INTERVAL);
  }

  private stopKeepAlive(): void {
    if (this.keepAliveTimer !== null) {
      clearInterval(this.keepAliveTimer);
      this.keepAliveTimer = null;
    }
  }
}
